import { Injectable } from '@angular/core';
import { AsyncSubject, Observable, distinctUntilChanged, map, noop } from 'rxjs';

import { ReportQueryParams, Reports } from '../models/report.model';
import { DistinctSubject, recursiveQuery } from '../models/utility.model';
import { AuthUsecase } from '../usecases/auth.usecase';
import { ReportGateway } from '../usecases/report.gateway';
import { ReportUsecase } from '../usecases/report.usecase';

@Injectable()
export class ReportInteractor extends ReportUsecase {
  get reports$(): Observable<Reports> {
    return this._reports;
  }

  private readonly _reports = new DistinctSubject<Reports>(new Reports());

  constructor(
    private _authUsecase: AuthUsecase,
    private _reportGateway: ReportGateway,
  ) {
    super();

    this._authUsecase.authState$
      .pipe(
        map(({ status }) => status === 'signedIn'),
        distinctUntilChanged(),
      )
      .subscribe(signedIn => (signedIn ? noop : this.onSignOut()));
  }

  listReports(params: ReportQueryParams): Observable<never> {
    const result = new AsyncSubject<never>();
    recursiveQuery(queryParams => this._reportGateway.listReports(queryParams), params).subscribe({
      next: reports => this._reports.next(new Reports(reports)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  getReport(projectId: string, version: number): Observable<string> {
    const result = new AsyncSubject<string>();
    this._reportGateway.getReport(projectId, version).subscribe({
      next: ({ url }) => result.next(url),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  private onSignOut(): void {
    this._reports.next(new Reports());
  }
}
